import BusinessIcon from "@mui/icons-material/Business";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonIcon from "@mui/icons-material/Person";
import { IconButton, ListItemButton, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import { forwardRef, useCallback, useState } from "react";

import { type ShiftSearchResult } from "../api/useSearchShift";

export type ShiftAction = "workplace" | "worker";

interface ShiftActionConfig {
  icon: React.ReactNode;
  label: string;
  id: string | undefined;
}

interface ShiftActionItemProps {
  shift: ShiftSearchResult;
  action: ShiftAction;
  selected: boolean;
  copied?: boolean;
  onClick: () => void;
}

export const ShiftActionItem = forwardRef<HTMLDivElement, ShiftActionItemProps>((props, ref) => {
  const { shift, action, selected, copied: copiedFromKeyboard = false, onClick } = props;
  const [copiedFromClick, setCopiedFromClick] = useState(false);

  const isCopied = copiedFromKeyboard || copiedFromClick;

  const config = {
    workplace: {
      icon: <BusinessIcon color="secondary" />,
      label: `Go to Workplace${shift.facility?.name ? ` (${shift.facility.name})` : ""}`,
      id: shift.facility?.userId,
    },
    worker: {
      icon: <PersonIcon color="primary" />,
      label: `Go to Worker${shift.agent?.name ? ` (${shift.agent.name})` : ""}`,
      id: shift.agent?.userId,
    },
  } satisfies Record<ShiftAction, ShiftActionConfig>;

  const { icon, label, id } = config[action];

  const handleCopy = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (id) {
        void navigator.clipboard.writeText(id);
        setCopiedFromClick(true);
        setTimeout(() => {
          setCopiedFromClick(false);
        }, 2000);
      }
    },
    [id]
  );

  return (
    <ListItemButton
      ref={ref}
      selected={selected}
      sx={{
        py: 0.75,
        pl: 6,
        pr: 2,
        "&.Mui-selected": {
          backgroundColor: "action.selected",
        },
      }}
      onClick={onClick}
    >
      <ListItemIcon sx={{ minWidth: 32 }}>{icon}</ListItemIcon>
      <ListItemText primary={label} primaryTypographyProps={{ variant: "body2" }} />
      <Tooltip title={isCopied ? "Copied!" : "Copy ID"}>
        <IconButton size="small" color={isCopied ? "success" : "default"} onClick={handleCopy}>
          {isCopied ? (
            <CheckIcon sx={{ fontSize: 16 }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Tooltip>
    </ListItemButton>
  );
});

ShiftActionItem.displayName = "ShiftActionItem";
